// i18n area 'auth' (TVirt task #127 layout, adopted for TVStor M1): sections
// login, user. MFA LANDED in the contract (#74 MFA-1 + MFA.2, 2026-08-18) and
// the login page HAS the OTP step for the enrolled user (#181 otp-web,
// ratifica Luca 2026-08-19 §4): a `401 mfa_required` opens the code field
// instead of pointing at the CLI. The strings below still distinguish the
// problem codes the backend already sends (mfa_enrollment_required,
// mfa_not_configured, the three 503s, the LDAP 422) instead of collapsing
// them into «wrong password» (#181 T2-T4).

export const en = {
  login: {
    welcome: 'Trantor TVStor v1',
    subtitle: 'Please sign-in to your account',
    username: 'Username',

    // #118 NAS-QUALNAMES (ratifica Luca 2026-08-14 §2): the name carries its
    // realm, always — so the page TEACHES the form before the server has to
    // refuse it, and the refusal below repeats the same sentence.
    usernamePlaceholder: 'REALM\\username or username@realm',
    usernameHint: 'Write the realm too — e.g. cluster1\\mario or mario@cluster1; local\\root for the break-glass account',
    qualifiedNameRequired: 'Write the realm too: REALM\\username or username@realm (local\\root for the break-glass account)',
    continue: 'Continue',
    changeUser: 'Not you?',
    realmKindLocal: 'local account',
    realmKindCluster: 'cluster account',
    realmKindFiles: 'file-services account',
    realmKindDirectory: 'directory account',
    password: 'Password',
    forgotPassword: 'Forgot password?',
    submit: 'Login',
    invalidCredentials: 'Invalid username or password',

    // #181 otp-web: the password VERIFIED (`401 mfa_required` is minted only
    // after the check — never an oracle) and the code step opens. The field
    // accepts BOTH shapes of the contract (auth.yaml `otp`): the 6-digit TOTP
    // code, or one single-use recovery code — the server tells them apart.
    otpPrompt:
      'This account requires a second factor. Enter the 6-digit code from your authenticator app — or one of your single-use recovery codes.',
    otpLabel: 'Verification code',
    otpHint: '6 digits from the authenticator app, or a recovery code (xxxxx-xxxxx)',
    otpSubmit: 'Verify',

    // The refusal of a sent code is the backend's UNIFORM `401 unauthenticated`
    // (auth.yaml: wrong/replayed/spent otp, all indistinguishable) — but here
    // the password verified seconds ago, so the honest reading names the code.
    otpRejected: 'The code was not accepted (wrong, expired or already used) — try again with a fresh code.',

    // MFA.2: obligation with an expired grace window and nothing enrolled —
    // there is no code to send; the ways out are administrative.
    mfaEnrollmentRequired:
      'This account is required to use a second factor, none was ever enrolled and the grace window has expired. An administrator must clear or renew the obligation.',

    // #74 MFA-1 409 mfa_not_configured: an ADMINISTRATOR error, named so it
    // can be repaired — never a silent fallback to password-only.
    mfaNotConfigured:
      'This account delegates its second factor to an external MFA server that is not configured (or has no usable TLS anchor). This is a cluster configuration problem — tell an administrator.',
    tooManyAttempts: 'Too many attempts — wait a moment and try again',

    // The three 503s of auth.yaml, kept distinct (#181 T3): the break-glass
    // advice is right ONLY for the store one.
    realmUnavailable: 'The realm cannot verify credentials right now — the cluster store is unreachable. Log in as a local user (break-glass).',
    directoryUnreachable: 'The directory did not answer — the login fails closed (no directory password is ever stored, so there is no offline web login). Retry once the directory is reachable.',
    mfaUnavailable: 'The external MFA server did not answer — the login fails closed. Retry in a moment.',
    serviceUnavailable: 'The realm cannot verify credentials right now (service unavailable). Retry in a moment.',

    // 422 directory_login_not_supported (NAS-2 Fase 6): the ldap-realm web
    // login is not built — the 422 is about the realm, not about the name.
    directoryLoginNotSupported: 'Web login is not available for LDAP directory realms yet — this realm can be used from SMB/SFTP clients, not from the console.',
    genericError: 'Login failed — is the TVStor backend reachable?'
  },
  user: {
    logout: 'Logout',
    password: {
      menu: 'Change password',
      title: 'Change your password',
      current: 'Current password',
      new: 'New password',
      confirm: 'Confirm new password',
      mismatch: 'The passwords do not match',
      error: 'Could not change the password',
      forcedTitle: 'Change your password to continue',
      forcedHint: 'This password was set by an administrator and must be replaced before entering.',
      forcedSubmit: 'Change and enter'
    },
    profile: {
      menu: 'Go to my profile'
    }
  },

  // #181 ondata 1b: the profile / account-security page behind the avatar menu
  // — MFA self-enrolment (QR + secret + recovery codes, confirm by possession),
  // voluntary password change, and the MFA.2 grace warning rendered VERBATIM
  // from what the login 201 affirmed (no invented countdown).
  profile: {
    title: 'My profile',
    summary: 'Your account and its security: password and the TOTP second factor.',
    securityTitle: 'Security',
    realm: 'Realm',

    // #189 GUI-RESIDUI: PATCH /users/myself — the self-writable subset is
    // displayName alone (any other field is refused regardless of grants).
    editAction: 'Edit display name',
    editTitle: 'Edit your profile',
    displayNameHint: 'The name shown instead of your username. This is the only field an account can change about itself — everything else belongs to an administrator.',
    rootNoProfile:
      'The break-glass session (root@local) has no account row behind it: profile, password and MFA live on managed accounts — the OS root password is the host\'s, changed on the host.',
    passwordHint: 'Changing it verifies the current password first; existing sessions stay valid.',
    mfa: {
      title: 'Second factor (TOTP)',
      enrolledChip: 'Enrolled',
      notEnrolledChip: 'Not enrolled',
      externalChip: 'External MFA',
      requiredChip: 'Second factor required',
      enrolledSince: 'A local TOTP second factor gates this login (enrolled {date}).',
      notEnrolled: 'No second factor is enrolled: the login is password-only.',
      external:
        'This account delegates its second factor to the cluster\'s external MFA server — a local TOTP enrolment is not available while the delegation is set.',
      requiredNote: 'An administrator requires a second factor on this account (marked {date}).',
      enroll: 'Enable the second factor',
      enrollTitle: 'Enable the second factor (TOTP)',
      enrollIntro:
        'Enrolling mints the secret for your authenticator app. The action is sudo-protected — confirm with your password to elevate.',
      enrollAction: 'Generate the secret',
      materialTitle: 'Scan the QR code with your authenticator app',
      materialHint:
        'Shown ONLY ONCE: the server keeps the secret sealed and the recovery codes hashed — nothing on this screen can be fetched again. Losing it before confirming simply means enrolling again.',
      secretLabel: 'Secret for manual entry (base32)',
      recoveryTitle: 'Recovery codes',
      recoveryHint:
        'Each code logs you in ONCE in place of a TOTP code — for the day the authenticator is lost. Store them now: they are shown only here.',
      copy: 'Copy',
      copied: 'Copied',
      confirmLabel: 'First code from the app',
      confirmHint:
        'Proves possession and activates the enrolment — until confirmed the login stays password-only. From the next login on: password + code.',
      confirmAction: 'Confirm and activate',
      confirmed: 'Second factor active: from the next login on, password + code.',
      pendingNote:
        'Closing now leaves the enrolment PENDING: it does not gate the login — enrol again to start over.',
      disenroll: 'Remove the second factor',
      disenrollTitle: 'Remove the second factor',
      disenrollBody:
        'Revokes the TOTP enrolment and destroys the sealed secret and the remaining recovery codes: the login returns password-only. Sudo-protected — confirm with your password.',
      disenrollObligationNote:
        'This account is REQUIRED to carry a second factor: removing it re-opens the enrolment obligation.',
      actionError: 'The MFA action was refused'
    },
    grace: {
      // Only what the server affirmed on the login 201 (MFA.2): deadline and
      // whole days left AS OF THAT LOGIN — no client-side countdown.
      warning:
        'This account must enrol a second factor. At login the server granted access until {deadline} — {days} whole days were left. Enrol from your profile before the deadline: past it, the login is refused until an administrator intervenes.',
      cta: 'Open my profile'
    }
  }
}

export const it: typeof en = {
  login: {
    welcome: 'Trantor TVStor v1',
    subtitle: 'Accedi al tuo account',
    username: 'Nome utente',
    usernamePlaceholder: 'REALM\\utente oppure utente@realm',
    usernameHint: 'Scrivi anche il realm — per esempio cluster1\\mario o mario@cluster1; local\\root per l\'account break-glass',
    qualifiedNameRequired: 'Scrivi anche il realm: REALM\\utente oppure utente@realm (local\\root per l\'account break-glass)',
    continue: 'Continua',
    changeUser: 'Non sei tu?',
    realmKindLocal: 'account locale',
    realmKindCluster: 'account del cluster',
    realmKindFiles: 'account dei servizi file',
    realmKindDirectory: 'account di directory',
    password: 'Password',
    forgotPassword: 'Password dimenticata?',
    submit: 'Accedi',
    invalidCredentials: 'Nome utente o password non validi',
    otpPrompt:
      'Questo account richiede un secondo fattore. Inserisci il codice a 6 cifre della tua app di autenticazione — oppure uno dei codici di recupero monouso.',
    otpLabel: 'Codice di verifica',
    otpHint: '6 cifre dall\'app di autenticazione, oppure un codice di recupero (xxxxx-xxxxx)',
    otpSubmit: 'Verifica',
    otpRejected: 'Il codice non è stato accettato (sbagliato, scaduto o già usato) — riprova con un codice nuovo.',
    mfaEnrollmentRequired:
      'Questo account è obbligato a usare un secondo fattore, non ne è mai stato configurato uno e il periodo di grazia è scaduto. Serve un amministratore per rimuovere o rinnovare l\'obbligo.',
    mfaNotConfigured:
      'Questo account delega il secondo fattore a un server MFA esterno non configurato (o senza un\'àncora TLS utilizzabile). È un problema di configurazione del cluster — avvisa un amministratore.',
    tooManyAttempts: 'Troppi tentativi — attendi un momento e riprova',
    realmUnavailable: 'Il realm non può verificare le credenziali in questo momento — lo store del cluster non è raggiungibile. Accedi come utente locale (break-glass).',
    directoryUnreachable: 'La directory non ha risposto — il login fallisce chiuso (nessuna password di directory viene mai conservata, quindi non esiste un login web offline). Riprova quando la directory è raggiungibile.',
    mfaUnavailable: 'Il server MFA esterno non ha risposto — il login fallisce chiuso. Riprova tra un momento.',
    serviceUnavailable: 'Il realm non può verificare le credenziali in questo momento (servizio non disponibile). Riprova tra un momento.',
    directoryLoginNotSupported: 'Il login web non è ancora disponibile per i realm directory LDAP — questo realm si usa dai client SMB/SFTP, non dalla console.',
    genericError: 'Accesso non riuscito — il backend TVStor è raggiungibile?'
  },
  user: {
    logout: 'Esci',
    password: {
      menu: 'Cambia password',
      title: 'Cambia la tua password',
      current: 'Password attuale',
      new: 'Nuova password',
      confirm: 'Conferma nuova password',
      mismatch: 'Le password non coincidono',
      error: 'Impossibile cambiare la password',
      forcedTitle: 'Cambia la password per continuare',
      forcedHint: 'Questa password è stata impostata da un amministratore e va sostituita prima di entrare.',
      forcedSubmit: 'Cambia ed entra'
    },
    profile: {
      menu: 'Vai al mio profilo'
    }
  },
  profile: {
    title: 'Il mio profilo',
    summary: 'Il tuo account e la sua sicurezza: password e secondo fattore TOTP.',
    securityTitle: 'Sicurezza',
    realm: 'Realm',

    // #189 GUI-RESIDUI: PATCH /users/myself — il sottoinsieme self-writable è
    // solo displayName (ogni altro campo è rifiutato a prescindere dai grant).
    editAction: 'Modifica nome visualizzato',
    editTitle: 'Modifica il tuo profilo',
    displayNameHint: 'Il nome mostrato al posto dello username. È l\'unico campo che un account può cambiare di sé stesso — tutto il resto appartiene a un amministratore.',
    rootNoProfile:
      'La sessione break-glass (root@local) non ha un account dietro: profilo, password e MFA vivono sugli account gestiti — la password di root è quella dell\'host e si cambia sull\'host.',
    passwordHint: 'Il cambio verifica prima la password attuale; le sessioni esistenti restano valide.',
    mfa: {
      title: 'Secondo fattore (TOTP)',
      enrolledChip: 'Attivo',
      notEnrolledChip: 'Non attivo',
      externalChip: 'MFA esterna',
      requiredChip: 'Secondo fattore obbligatorio',
      enrolledSince: 'Un secondo fattore TOTP locale protegge questo accesso (attivato il {date}).',
      notEnrolled: 'Nessun secondo fattore attivo: l\'accesso è a sola password.',
      external:
        'Questo account delega il secondo fattore al server MFA esterno del cluster — l\'attivazione TOTP locale non è disponibile finché la delega è impostata.',
      requiredNote: 'Un amministratore richiede un secondo fattore su questo account (marcato il {date}).',
      enroll: 'Attiva il secondo fattore',
      enrollTitle: 'Attiva il secondo fattore (TOTP)',
      enrollIntro:
        'L\'attivazione genera il segreto per la tua app di autenticazione. L\'azione è protetta da sudo — conferma con la tua password per elevarti.',
      enrollAction: 'Genera il segreto',
      materialTitle: 'Inquadra il QR con la tua app di autenticazione',
      materialHint:
        'Mostrato UNA SOLA VOLTA: il server conserva il segreto sigillato e i codici di recupero solo come hash — niente di questa schermata è recuperabile dopo. Perderla prima della conferma significa semplicemente rifare l\'attivazione.',
      secretLabel: 'Segreto per inserimento manuale (base32)',
      recoveryTitle: 'Codici di recupero',
      recoveryHint:
        'Ogni codice fa entrare UNA volta al posto del codice TOTP — per il giorno in cui l\'authenticator si perde. Conservali ora: compaiono solo qui.',
      copy: 'Copia',
      copied: 'Copiato',
      confirmLabel: 'Primo codice dall\'app',
      confirmHint:
        'Prova il possesso e attiva l\'arruolamento — finché non confermi l\'accesso resta a sola password. Dal prossimo accesso: password + codice.',
      confirmAction: 'Conferma e attiva',
      confirmed: 'Secondo fattore attivo: dal prossimo accesso, password + codice.',
      pendingNote:
        'Chiudendo ora l\'arruolamento resta IN SOSPESO: non protegge l\'accesso — per ricominciare basta attivare di nuovo.',
      disenroll: 'Rimuovi il secondo fattore',
      disenrollTitle: 'Rimuovi il secondo fattore',
      disenrollBody:
        'Revoca l\'arruolamento TOTP e distrugge il segreto sigillato e i codici di recupero rimasti: l\'accesso torna a sola password. Protetto da sudo — conferma con la tua password.',
      disenrollObligationNote:
        'Questo account è OBBLIGATO ad avere un secondo fattore: rimuoverlo riapre l\'obbligo di arruolamento.',
      actionError: 'L\'azione MFA è stata rifiutata'
    },
    grace: {
      warning:
        'Questo account deve attivare un secondo fattore. Al login il server ha concesso l\'accesso fino al {deadline} — mancavano {days} giorni interi. Attivalo dal tuo profilo prima della scadenza: oltre, l\'accesso viene rifiutato finché non interviene un amministratore.',
      cta: 'Apri il mio profilo'
    }
  }
}
